/**
 * @fileoverview Input/output schemas for the a-storyline-extractor-v1 agent.
 *
 * This agent is the first half of a split storyline extraction pipeline.
 * It focuses exclusively on extracting the A-Storyline(s) — the primary
 * external conflict(s) — with thorough beat coverage scaled to the story
 * type. Most stories have one A-Storyline, but long novels and epics may
 * have two or more parallel main plots (see Storyline Count guidance).
 *
 * Output schema is flat (3 levels max) for Gemini API compatibility.
 * Beats reference their A-Storyline by 0-based index.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { StructurePhaseSchema } from '@/domains/story-planning/schemas/structure-phases';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = BaseFlowInputSchema.extend({
  synopsisInput: z.string().describe('The condensed synopsis or full text to analyze.'),
  isFullText: z.boolean().optional().describe('When true, the synopsisInput contains the full story text rather than a condensed synopsis. Adjusts prompt framing accordingly.'),
  storyType: z.string().optional().describe('The story type (e.g., "Novelette", "Novel", "Epic"). Used to calibrate storyline beat density.'),
  projectedWordCount: z.number().optional().describe('The target word count for the story. Used to calibrate storyline beat density.'),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA (Flat structure for Gemini compatibility)
// =============================================================================

/**
 * An A-Storyline extracted from the text. Most stories have one; long novels
 * and epics may have multiple parallel A-Storylines.
 */
const StorylineOutputSchema = z.object({
  name: z.string().describe(
    'Short, evocative name for the A-Storyline (2-4 words). ' +
    'Named after the main goal or conflict ("The Ark Hunt", "The Rebellion", "The Escape").'
  ),
  summary: z.string().describe(
    'A brief description of the A-Storyline (1-3 sentences). ' +
    'Captures how this main conflict starts, develops, and resolves at a high level.'
  ),
  priority: z.literal('A').describe(
    'Always "A" — this agent only extracts main (A-priority) storylines.'
  ),
});

/**
 * Beat with storylineIndex reference into the A-Storylines array.
 * Multiple beats per structural phase for thorough coverage, scaled to story type.
 */
const BeatOutputSchema = z.object({
  storylineIndex: z.number().describe('Index of the A-storyline this beat belongs to (0-based, referencing the storylines array). 0 when there is a single A-Storyline; 0/1/2 when multiple.'),
  storylinePhase: StructurePhaseSchema.describe(
    'The structural function this beat serves for THIS STORYLINE\'s internal arc. ' +
    'For A-storylines, this always equals globalPhase since A-Stories define the global timeline.'
  ),
  globalPhase: StructurePhaseSchema.describe(
    'Where this beat falls in the OVERALL story timeline. ' +
    'For A-storylines, globalPhase always equals storylinePhase.'
  ),
  order: z.number().min(0).default(0).describe(
    'Ordering index (integer >= 0) when multiple beats share the same globalPhase within the same storyline (0-based). ' +
    'Use 0 for a single beat, 0/1/2/3 for multiple beats within one phase of one storyline.'
  ),
  plotEvent: z.string().describe(
    'The concrete, observable event that occurs in the story world. ' +
    'Focus on what physically happens—actions, discoveries, confrontations, arrivals, departures.'
  ),
  narrativeState: z.string().optional().describe(
    'The emotional or thematic state of this A-Storyline at this moment (optional).'
  ),
  goalProgress: z.number().min(-1).max(1).describe(
    'Progress toward this A-Storyline\'s central objective. ' +
    '+1 = Major advancement. -1 = Significant setback. 0 = Neutral.'
  ),
});

/**
 * Story question for an A-Storyline, tagged by storylineIndex.
 */
const StoryQuestionOutputSchema = z.object({
  storylineIndex: z.number().describe('Index of the A-Storyline this question belongs to (0-based, referencing the storylines array).'),
  question: z.string().describe('The dramatic question about this storyline\'s main conflict (e.g., "Will the hero find the Ark before the Nazis?")'),
  status: z.enum(['open', 'answered']).default('open').describe('Whether this question is still open or has been answered in the text'),
});

/**
 * Output schema for the a-storyline-extractor-v1 agent.
 *
 * Flat structure (3 levels max) for Gemini API compatibility.
 * Contains one or more A-Storylines with thorough beat coverage per storyline
 * (refer to the Storyline Count guidance inlined in the prompt).
 */
export const OutputSchema = z.object({
  storylines: z.array(StorylineOutputSchema).describe(
    'The A-Storylines extracted from the narrative. Usually one, but long novels and epics may have multiple parallel main plots (see Storyline Count guidance in the prompt). Only main external conflicts — subplots belong to B/C agents.'
  ),
  beats: z.array(BeatOutputSchema).describe(
    'All beats across all A-Storylines, covering the 7 structural phases. ' +
    'Beat count should match the story type — see prompt guidance (targets apply per A-Storyline). ' +
    'Every phase should have at least one beat per A-Storyline if the text provides enough information.'
  ),
  storyQuestions: z.array(StoryQuestionOutputSchema).describe(
    'Dramatic questions across all A-Storylines, each tagged with its storylineIndex. These are the big hooks about each main conflict.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;

// Type exports for consumer convenience
export type StorylineOutput = z.infer<typeof StorylineOutputSchema>;
export type BeatOutput = z.infer<typeof BeatOutputSchema>;
export type StoryQuestionOutput = z.infer<typeof StoryQuestionOutputSchema>;
